import {UIComponent} from "@webfruits/core";
import {SVGComponent} from "@webfruits/toolbox/dist/components/svg/SVGComponent";
import {HotspotIllustrationElement} from "./HotspotIllustrationElement";
import {AppConfig} from "../../../config/AppConfig";
import {gsap} from "gsap";

/******************************************************************
 * HotspotIllustration
 *
 * @author matthias.schulz@jash.de
 *****************************************************************/

export class HotspotIllustration extends UIComponent {

    /******************************************************************
     * Properties
     *****************************************************************/

    private _svgComponent: SVGComponent;
    private _svgElements: SVGElement[];
    private _imageElements: HotspotIllustrationElement[] = [];
    private _imagesFrame: UIComponent;
    private _hoveringHelper = {pi: 0};

    /******************************************************************
     * Constructor
     *****************************************************************/

    constructor(private _svgSource: string) {
        super("hotspot-illustration");
        this.initSVGComponent();
        this.initConversionToHTMLElements();
        this.playLeaveAnimation();
    }

    /******************************************************************
     * Public Methodes
     *****************************************************************/

    get aspectRatio(): number {
        return this._svgComponent.defaultWidth / this._svgComponent.defaultHeight;
    }

    public playEnterAnimation() {
        this.stopHoveringAnimation();
        this._imageElements.forEach((element) => {
            element.playEnterAnimation();
        })
    }

    public playLeaveAnimation() {
        this.playHoveringAnimation();
        this._imageElements.forEach((element) => {
            element.playLeaveAnimation();
        })
    }

    public playHoveringAnimation() {
        if (gsap.isTweening(this._hoveringHelper)) {
            return;
        }
        const playCircleAnimation = () => {
            const ampX = 5;
            const ampY = 10;
            this._hoveringHelper.pi = 0;
            gsap.to(this._hoveringHelper, {
                duration: Math.random() * 2 + 2,
                pi: Math.PI * 2,
                onUpdate: () => {
                    gsap.to(this._imagesFrame.view, {
                        duration: 0.5,
                        x: Math.sin(this._hoveringHelper.pi) * ampX,
                        y: Math.cos(this._hoveringHelper.pi) * ampY,
                        ease: "power4.out"
                    })
                },
                ease: "linear.inOut",
                onComplete: () => playCircleAnimation()
            })
        }
        playCircleAnimation();
    }

    private stopHoveringAnimation() {
        gsap.killTweensOf(this._hoveringHelper);
        gsap.to(this._imagesFrame.view, {
            duration: AppConfig.HOTSPOT_HOVER_DURATION,
            overwrite: true,
            x: 0,
            y: 0,
            ease: "sine.out",
        })
    }

    public updateStyles() {
        this.applyStyle({
            position: "absolute",
            width: this._svgComponent.defaultWidth,
            height: this._svgComponent.defaultHeight
        })
        this._imagesFrame.applyStyle({
            position: "absolute",
            width: "100%",
            height: "100%"
        })
    }

    /******************************************************************
     * Private Methodes
     *****************************************************************/

    private initSVGComponent() {
        this._svgComponent = new SVGComponent(this._svgSource);
        this._svgElements = Array.from(Array.from(this._svgComponent.svgRoot.children)[0].children) as SVGElement[];
    }

    private initConversionToHTMLElements() {
        this._imagesFrame = new UIComponent("images-frame");
        this.addChild(this._imagesFrame);
        this._svgElements.forEach((svgElement: SVGElement) => {
            const element = new HotspotIllustrationElement(svgElement, this._svgComponent)
            this._imageElements.push(element);
            this._imagesFrame.addChild(element);
        })
    }

    /******************************************************************
     * Events
     *****************************************************************/

    // no events yet


}
